/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';

import { Breadcrumbs, Grid, Typography } from '@mui/material';

import { DeletableThumbnailBadge } from '../../components/DeletableThumbnailBadge/DeletableThumbnailBadge';
import { DescriptionPanel } from '../../components/DescriptionPanel/DescriptionPanel';
import { EntitiesItemNew } from '../../components/EntitiesItenNew/EntitiesItemNew';
import { EntityThumbnail } from '../../components/EntityThumbnail/EntityThumbnail';
import { useUpdateOrganization } from '../../components/hooks/useUpdateOrganization';
import { ProductsList } from '../../components/ProductsList/ProductsList';
import { ProductsListItem } from '../../components/ProductsList/ProductsListItem';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { OrganizationApiService } from '../../services/OrganizationApiService';
import { RootState } from '../../store';
import { ApiClientError } from '../../types/api/ApiClientError';
import { EntityId } from '../../types/api/EntityId';
import { UpdateOrganizationRequest } from '../../types/organization/UpdateOrganizationRequest';
import { ProductStatus } from '../../types/product/ProductStatus';
import { readFile } from '../../utils/canvasUtils';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchDashboardForOrganization, fetchOrganization } from '../../utils/queryFns';
import { reactToDeleteOrganization, reactToUpdateOrganizationThumbnail } from '../../utils/reactions';
import { useApiValidation } from '../../utils/useApiValidation';
import { useControlOverlays } from '../../utils/useControlOverlays';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useIsMounted } from '../../utils/useIsMounted';
import { useOrganizationPermissions } from '../../utils/useOrganizationPermissions';
import { createOrganizationPageStyles } from './OrganizationPage.styles';

type PathParamsType = {
  organization_id: string;
};
const mapStateToProps = (state: RootState) => ({
  organizations: state.organizations,
});
const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type IProps = PropsFromRedux;

const OrganizationPageComponent = memo<IProps>(() => {
  const { t } = useTranslation([Namespaces.OrganizationAdmin, Namespaces.Navigation]);
  const { doOpenCreateProjectOverlay } = useControlOverlays();
  const classes = createOrganizationPageStyles();
  const navigate = useNavigate();
  const params = useParams<PathParamsType>();
  const organizationId = params.organization_id!;
  const permissions = useOrganizationPermissions(organizationId);
  const { errorMessage, setApiError, resetApiError } = useApiValidation();

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const handleProjectClick = (project: { id: string }) => {
    void navigate(
      prepareRoutePath(BasicRoutes.PROJECT_PAGE, {
        ':organization_id': organizationId,
        ':project_id': project.id,
      }),
    );
  };

  const onConfirmDelete = async () => {
    setIsOpenModal(false);
    try {
      await OrganizationApiService.deleteOrganizationById({
        organizationId,
      });
      void navigate(prepareRoutePath(BasicRoutes.HOME));
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
      setIsOpenErrorModal(true);
    } finally {
      reactToDeleteOrganization();
    }
  };

  const handleManageClick = () => {
    void navigate(
      prepareRoutePath(BasicRoutes.MANAGE_ORGANIZATION, {
        ':organization_id': organizationId,
      }),
    );
  };

  const organizationQuery = useQuery([fetchOrganization.key, { organizationId }], fetchOrganization.fn);
  const organization = useMemo(() => organizationQuery.data?.data.payload, [organizationQuery.data]);

  useDocumentTitle(organization?.name);

  const organizationIsFetching = organizationQuery.status === 'loading';

  const dashboardQuery = useQuery(
    [fetchDashboardForOrganization.key, { organizationId }],
    fetchDashboardForOrganization.fn,
  );

  const projects = useMemo(() => dashboardQuery.data?.data.payload.projects || [], [dashboardQuery.data]);
  const isFetching = useMemo(() => dashboardQuery.status === 'loading', [dashboardQuery.status]);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [isOpenPermissionsErrorModal, setIsOpenPermissionsErrorModal] = useState(false);
  const [isOpenDelModal, setIsOpenDelModal] = useState<boolean>(false);

  useEffect(() => {
    if (!permissions.read_organization) {
      setIsOpenPermissionsErrorModal(true);
    }
  }, [permissions]);

  const isMounted = useIsMounted();

  const [updateOrganization] = useUpdateOrganization(true);

  const onEditSubmit = async (values: UpdateOrganizationRequest) => {
    if (organization) {
      try {
        resetApiError();
        const data: UpdateOrganizationRequest = {};
        if (values.name !== undefined) {
          data.name = values.name.trim();
        }
        if (values.description !== undefined) {
          data.description = values.description?.trim() || null;
        }
        await updateOrganization({ organizationId: organization.id, data });
        if (!isMounted.current) {
          return;
        }
      } catch (e) {
        if (!isMounted.current) {
          return;
        }
        const error = e as ApiClientError;
        if (error.response) {
          setApiError(error);
        }
        console.error(error);
      }
    }
  };

  const uploadThumbnail = async (thumbnail: Blob) => {
    if (thumbnail && organizationId) {
      setIsDisabled(true);
      try {
        await OrganizationApiService.updateOrganizationThumbnail(organizationId, {
          type: 'base64',
          base64: await readFile(thumbnail),
        });
        await reactToUpdateOrganizationThumbnail({ organizationId });
      } catch (e) {
        console.error(e);
        setIsOpenErrorModal(true);
      } finally {
        setIsDisabled(false);
      }
    }
  };

  const onConfirmDeleteImage = async () => {
    setIsOpenDelModal(false);
    setIsDisabled(true);
    try {
      await OrganizationApiService.deleteOrganizationThumbnail(organizationId);
      await reactToUpdateOrganizationThumbnail({ organizationId });
    } catch (e) {
      const error = e as ApiClientError;
      console.log(error);
      setIsOpenErrorModal(true);
    } finally {
      setIsDisabled(false);
    }
  };

  const hideDeleteAvatarBtn = useMemo(() => !organization?._links.thumbnail, [organization]);

  const onOkPermissionsError = () => {
    setIsOpenPermissionsErrorModal(false);
    void navigate(-1);
  };

  const products = (projectId: EntityId) => {
    const statistics: ProductStatus = {
      Running: 0,
      Creating: 0,
      Updating: 0,
      Importing: 0,
      Unavailable: 0,
      Error: 0,
    };

    if (!isFetching && projects) {
      projects
        .find(project => project.id === projectId)
        ?.products.forEach(product => {
          (Object.keys(statistics) as Array<keyof ProductStatus>).forEach(key => {
            statistics[key] = statistics[key] + (product[key] ?? 0);
          });
        });
    }
    return statistics;
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} classes={{ item: classes.wrapperBreadcrumbs }}>
          <Breadcrumbs>
            <RouterLink to="/">{t('navigation:page.home')}</RouterLink>
            <Typography>{!organizationIsFetching && organization?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item xs={12} classes={{ item: classes.wrapperEditBoxPanel }}>
          <DescriptionPanel
            name={organization?.name || ''}
            description={organization?.description || null}
            icon={
              <DeletableThumbnailBadge
                overlap="rectangular"
                invisible={hideDeleteAvatarBtn}
                onDelBtnClick={() => setIsOpenDelModal(true)}
                disabled={isDisabled}
                thumbnail={
                  <EntityThumbnail
                    size={56}
                    loaderSize={34}
                    type="organization"
                    organizationId={organizationId}
                    {...(permissions.edit_organization
                      ? {
                          allowUploadImage: true,
                          onImageUpload: uploadThumbnail,
                        }
                      : { allowUploadImage: false })}
                  />
                }
              />
            }
            isLoading={organizationIsFetching}
            isEditable={permissions.edit_organization}
            onSubmit={onEditSubmit}
            handleManage={handleManageClick}
            isDeletable={permissions.delete_organization}
            handleDelete={() => setIsOpenModal(true)}
          />
        </Grid>
        <Grid item xs={12}>
          <ProductsList
            title={t('departments.your_projects')}
            loading={isFetching}
            isEmpty={!projects.length}
            noItemsTitle={t('departments.no_projects')}
          >
            <>
              {projects.map(project => (
                <ProductsListItem
                  key={project.id}
                  name={project.name}
                  products={products(project.id)}
                  icon={
                    <EntityThumbnail
                      size={24}
                      type="project"
                      organizationId={organizationId}
                      projectId={project.id}
                      allowUploadImage={false}
                    />
                  }
                  onClick={() => handleProjectClick(project)}
                  noItemsText={t('departments.products.no_products_in_project')}
                />
              ))}
              <EntitiesItemNew
                title={t(`${Namespaces.Entities}:project.create`)}
                disabledTitle={t(`${Namespaces.Entities}:project.not_allowed`)}
                disabled={!permissions.create_project}
                onClick={doOpenCreateProjectOverlay}
              />
            </>
          </ProductsList>
        </Grid>
      </Grid>
      {permissions.delete_organization && (
        <>
          <SimpleConfirmModal
            open={isOpenModal}
            message={t(`${Namespaces.Common}:confirmation.delete_organization`, { name: organization?.name })}
            onCancel={() => setIsOpenModal(false)}
            onConfirm={onConfirmDelete}
          />
          <SimpleNotifyModal
            open={isOpenErrorModal}
            message={t(
              [
                `${Namespaces.Common}:leave_organization_errors.${errorMessage}`,
                `${Namespaces.Common}:errors.unexpected_error`,
              ],
              { organization_name: organization?.name },
            )}
            onOk={() => setIsOpenErrorModal(false)}
          />
          <SimpleConfirmModal
            open={isOpenDelModal}
            message={t('organizations.action_delete_thumbnail.content_text')}
            onCancel={() => setIsOpenDelModal(false)}
            onConfirm={onConfirmDeleteImage}
          />
        </>
      )}
      <SimpleNotifyModal
        open={isOpenPermissionsErrorModal}
        message={t(`${Namespaces.Rbac}:organization.error`)}
        onOk={onOkPermissionsError}
      />
    </>
  );
});

export const OrganizationPage = connector(OrganizationPageComponent);
